import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { SellerToList } from './types';
import { Menu } from 'iconsax-react';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import InfoText from '../atoms/InfoText';

export const useSellersColumns = (onDeleteSeller: (id: string) => void): ColumnsType<SellerToList> => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return [
    {
      title: <span data-testid="col-seller-id-title" className="text-[16px] font-semibold font-sans text-[#090909] dark:text-dark-text-primary">{t('sellers.sellerId')}</span>,
      dataIndex: 'sellerId',
      key: 'sellerId',
      render: (text, _, index) => <span data-testid={`cell-seller-id-${index}`} className="text-[14px] font-normal font-sans text-[#090909] dark:text-dark-text-primary">{text}</span>,
    },
    {
      title: <span data-testid="col-seller-name-title" className="text-[16px] font-semibold font-sans text-[#090909] dark:text-dark-text-primary">{t('sellers.sellerName')}</span>,
      dataIndex: 'name',
      key: 'name',
      render: (text, _, index) => <span data-testid={`cell-seller-name-${index}`} className="text-[14px] font-normal font-sans text-[#090909] dark:text-dark-text-primary">{text}</span>,
    },
    {
      title: <span data-testid="col-seller-email-title" className="text-[16px] font-semibold font-sans text-[#090909] dark:text-dark-text-primary">{t('sellers.email')}</span>,
      dataIndex: 'email',
      key: 'email',
      render: (text, _, index) => <span data-testid={`cell-seller-email-${index}`} className="text-[14px] font-normal font-sans text-[#090909] dark:text-dark-text-primary">{text}</span>,
    },
    {
      title: <span data-testid="col-seller-profile-complete-title" className="text-[16px] font-semibold font-sans text-[#090909] dark:text-dark-text-primary">{t('sellers.profileComplete')}</span>,
      dataIndex: 'profileComplete',
      key: 'profileComplete',
      render: (completed: boolean, _, index) => {
        return completed ? (
          <div
            data-testid={`cell-seller-profile-complete-${index}`}
            className="w-[96px] h-[35px] py-[8px] px-[12px] bg-[#F2F9F4] text-[#4DA167] rounded-[8px] flex items-center justify-center font-sans text-[14px] font-normal leading-[100%]"
          >
            {t('sellers.completed')}
          </div>
        ) : (
          <div
            data-testid={`cell-seller-profile-complete-${index}`}
            className="w-[145px] h-[35px] py-[8px] px-[12px] bg-[#FEF6F2] text-[#F58F29] rounded-[8px] flex items-center justify-center font-sans text-[14px] font-normal leading-[100%]"
          >
            {t('sellers.notCompletedYet')}
          </div>
        );
      },
    },
    {
      title: <span data-testid="col-seller-date-title" className="text-[16px] font-semibold font-sans text-[#090909] dark:text-dark-text-primary">{t('sellers.dateApplied')}</span>,
      dataIndex: 'dateApplied',
      key: 'dateApplied',
      render: (text, _, index) => <span data-testid={`cell-seller-date-${index}`} className="text-[14px] font-normal font-sans text-[#090909] dark:text-dark-text-primary">{text}</span>,
    },
    {
      title: <span data-testid="col-seller-action-title" className="text-[16px] font-semibold font-sans text-[#090909] dark:text-dark-text-primary">{t('sellers.action')}</span>,
      key: 'action',
      render: (_, record, index) => {
        const actionsConfig = [
          {
            key: 'setup-marketplace',
            label: t('sellers.sellerProfile.actions.setupMarketplace'),
            visible: !!record.actions?.canSetupMarketPlace,
            onClick: () => navigate(`/sellers/${record.id}/profile/setup-marketplace`),
          },
          {
            key: 'add-product',
            label: t('sellers.sellerProfile.actions.addProduct'),
            visible: !!record.actions?.addProduct,
            onClick: () => navigate(`/sellers/${record.id}/profile/add-product`),
          },
          {
            key: 'edit-profile',
            label: t('sellers.sellerProfile.actions.editProfile'),
            visible: !!record.actions?.editProfile,
            onClick: () => navigate(`/sellers/${record.id}/profile-creation?mode=edit`),
          },
          {
            key: 'delete-profile',
            label: t('sellers.sellerProfile.actions.deleteProfile'),
            visible: !!record.actions?.deleteProfile,
            danger: true,
            onClick: () => onDeleteSeller(record.id),
          },
        ];

        const availableItems: MenuProps['items'] = actionsConfig
          .filter((item) => item.visible)
          .map(({ key, label, danger, onClick }) => ({
            key,
            label,
            danger,
            onClick: (info) => {
              info.domEvent.stopPropagation();
              onClick();
            },
          }));

        const menuItems: MenuProps['items'] =
          availableItems.length > 0
            ? availableItems
            : [
              {
                key: 'no-actions',
                label: (
                  <div
                    className="px-3 py-1.5 cursor-default select-none pointer-events-none"
                    data-testid={`action-no-actions-${record.id || index}`}
                  >
                    <InfoText
                      text={t('sellers.noActions')}
                      className="!text-[14px] !font-normal text-light-text-placeholder dark:text-dark-text-placeholder"
                    />
                  </div>
                ),
                disabled: true,
                className:
                  '!p-0 !bg-transparent hover:!bg-transparent cursor-default pointer-events-none',
              },
            ];

        return (
          <div
            className="flex cursor-pointer text-[#090909] dark:text-dark-text-primary"
            data-testid={`cell-seller-action-${index}`}
          >
            <Dropdown menu={{ items: menuItems }} trigger={['click']}>
              <div
                className='hover:bg-pink-500 [&.ant-dropdown-open]:bg-pink-500 rounded-lg p-1 hover:cursor-pointer'
                data-testid={`action-btn-${index}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Menu size="20" className="!text-[#090909] dark:!text-dark-text-primary" />
              </div>
            </Dropdown>
          </div>
        );
      },
    },
  ];
};
